import { LitElement, html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { property, query, state } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import type { Popup } from "maplibre-gl"; // type only — erased at build time
import type {
  FeatureInfoResult,
  LayerUIState,
  Map0Config,
  Map0Core,
  MeasureController,
  MeasureMode,
  MeasureState,
  NormalizedConfig,
  SearchResult,
  ValidationError,
} from "@map0/core";
import { normalizeConfig, resolveConfigExtends, validateConfig, type TocNode } from "@map0/schema";
import { tocLeafIds, tocShowIds, tocVisibility } from "@map0/schema/toc";
import type { Map0ViewerElement, Map0ViewerEventDetails } from "./element.js";
import type { Map0BottomSheet } from "./bottom-sheet.js"; // type only — the sheet ships with the popup chunk
import { componentStyles } from "./styles.js";
import { attachDropZone } from "./file-drop.js";
import { filterTerms, highlightSegments, matchesAll, unmatchedTerms } from "./toc-filter.js";

/**
 * Everything the map itself needs — the engine, MapLibre and its stylesheet, the
 * popup renderer with its sanitiser — is behind these imports, so a page that
 * embeds a map far below the fold downloads none of it until the map is needed.
 * Type-only imports above are erased at build time and cost nothing.
 */
const loadEngine = () => import("@map0/core");
const loadPopupRenderer = () => import("./popup.js");
const loadMaplibreCss = () => import("./maplibre-css.js");
/* The dialogs — and what they drag in, capabilities parsing and the print
   composer — are loaded the first time a user opens them, not on page load. */
const loadDialog = {
  add: () => import("./add-layer-dialog.js"),
  print: () => import("./print-dialog.js"),
};

type PopupRenderer = Awaited<ReturnType<typeof loadPopupRenderer>>;

/** one constructable sheet for MapLibre's CSS, shared by every viewer on the page */
let maplibreSheet: CSSStyleSheet | undefined;

const CONTROL_SVGS = {
  print:
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:auto"><path d="M6 9V3h12v6"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8" rx="1"/></svg>',
  share:
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:auto"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 10.5 6.8-3.9M8.6 13.5l6.8 3.9"/></svg>',
  measure:
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block;margin:auto"><path d="M3 15 15 3l6 6L9 21z"/><path d="m7 11 2 2"/><path d="m10 8 2 2"/><path d="m13 5 2 2"/></svg>',
} as const;

class IconButtonControl {
  private container?: HTMLElement;
  constructor(
    private readonly icon: keyof typeof CONTROL_SVGS,
    private readonly onClick: () => void,
    private readonly label: string,
  ) {}
  onAdd(): HTMLElement {
    const div = document.createElement("div");
    div.className = "maplibregl-ctrl maplibregl-ctrl-group";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.title = this.label;
    btn.setAttribute("aria-label", this.label);
    btn.innerHTML = CONTROL_SVGS[this.icon];
    btn.addEventListener("click", this.onClick);
    div.appendChild(btn);
    this.container = div;
    return div;
  }
  onRemove(): void {
    this.container?.remove();
  }
}

const RADII = { none: "0px", sm: "6px", md: "10px", lg: "16px" } as const;
const RADII_SM = { none: "0px", sm: "4px", md: "7px", lg: "10px" } as const;
/**
 * Below this width of the viewer itself (not the window — N3) feature info
 * opens as a bottom sheet instead of an anchored popup (F5.5). A JS decision,
 * not a container query: the two are different DOM (a MapLibre popup vs. our
 * own element), so the width is observed rather than styled.
 */
const SHEET_BREAKPOINT = 640;
/**
 * At most this wide, every panel docks in the right-hand column: two columns
 * (56px controls + 300px + gap + 300px + 10px) and the search field no longer
 * fit side by side. styles.ts keys its rules for this to `max-width: 687px`.
 */
const COMPACT_MAX = 687;

/** layers from which the panel offers a filter box when `filter` is "auto" (F2.7) */
const TOC_FILTER_AUTO = 10;

/** a panel's corner, split the way the layout reads it: the edge it docks to, and which end */
interface Corner {
  edge: "left" | "right";
  end: "top" | "bottom";
}
const EDGES = ["left", "right"] as const;
const corner = (position: string): Corner => ({
  edge: position.endsWith("left") ? "left" : "right",
  end: position.startsWith("bottom") ? "bottom" : "top",
});

/** the edges with a dock column, as `data-dock` lists them — compact viewers have the right one only */
const dockSides = (corners: Corner[], compact: boolean): string => {
  if (corners.length === 0) return "";
  if (compact) return "right";
  return EDGES.filter((edge) => corners.some((c) => c.edge === edge)).join(" ");
};

/** a title with the filter's hits marked */
const highlight = (text: string, terms: string[]): unknown =>
  terms.length === 0
    ? text
    : highlightSegments(text, terms).map((s) => (s.hit ? html`<mark>${s.text}</mark>` : s.text));

const icons = {
  layers: html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 17 9 5 9-5"/></svg>`,
  chevron: html`<svg class="chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>`,
  info: html`<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-5"/><path d="M12 8h.01"/></svg>`,
  legend: html`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="6" height="6" rx="1"/><path d="M13 7h8"/><rect x="3" y="14" width="6" height="6" rx="1"/><path d="M13 17h8"/></svg>`,
  plus: html`<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>`,
  search: html`<svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>`,
  pin: html`<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z"/><circle cx="12" cy="10" r="2.5"/></svg>`,
};

/**
 * `<map0-viewer>` — the map0 web component.
 *
 * Config sources (first match wins):
 *  1. `config` property (JS object)
 *  2. `config-src` attribute (URL)
 *  3. inline `<script type="application/json">` child
 */
export class Map0Viewer extends LitElement implements Map0ViewerElement {
  static override styles = [componentStyles];

  @property({ attribute: false }) config?: Map0Config;
  @property({ attribute: "config-src" }) configSrc?: string;
  /**
   * When to start loading — mirrors `<img loading>`. `"lazy"` (default) waits
   * until the element is near the viewport; `"eager"` starts immediately.
   * This is an attribute rather than a config key on purpose: it decides
   * whether the config is fetched at all.
   */
  @property({ attribute: "loading" }) loading: "lazy" | "eager" = "lazy";
  /**
   * Host-page override for the colour scheme. Unset, the config's `theme.mode`
   * decides; `theme="dark"` / `theme="light"` wins over it, and flipping the
   * attribute restyles a running map. This is the hook for a host page with its
   * own dark-mode toggle — the config cannot know what the page around it did.
   */
  @property({ attribute: "theme" }) theme?: "light" | "dark";

  @state() private _errors: ValidationError[] | null = null;
  @state() private _fatal: string | null = null;
  @state() private _layers: LayerUIState[] = [];
  @state() private _basemapId = "";
  @state() private _tocOpen = true;
  @state() private _legendOpen = false;
  @state() private _addOpen = false;
  @state() private _printOpen = false;
  @state() private _dialogLoading: "add" | "print" | null = null;
  /** a file drag is over the viewer — the drop zone overlay is showing (F3.2) */
  @state() private _dropActive = false;
  @state() private _groupCollapsed: Record<string, boolean> = {};
  /** text in the layer panel's filter box (F2.7) */
  @state() private _tocFilter = "";
  @state() private _ready = false;
  /** true once initialisation actually starts — before that the element only reserves space */
  @state() private _loading = false;
  @state() private _notices: Array<{ id: number; kind: "error" | "info"; text: string }> = [];
  @state() private _hover: { x: number; y: number; html: string } | null = null;
  @state() private _searchResults: SearchResult[] = [];
  @state() private _searchBusy = false;
  @state() private _searchActive = -1;
  @state() private _searchExpanded = false;
  @state() private _searchEmpty = false;
  @state() private _measure: MeasureState | null = null;
  @state() private _measureLoading = false;
  /** the viewer is narrower than SHEET_BREAKPOINT — feature info docks at the bottom */
  @state() private _narrow = false;
  /** the viewer is at most COMPACT_MAX wide — the panels share one column */
  @state() private _compact = false;
  /** feature info shown as a bottom sheet (narrow viewers); null = none open */
  @state() private _sheet: { label: string; content: HTMLElement } | null = null;

  private measureController?: MeasureController;

  private noticeSeq = 0;
  private statusSeen = new Map<string, string>();
  private searchSeq = 0;
  private searchTimer?: ReturnType<typeof setTimeout>;
  private searchAbort?: AbortController;

  @query(".map") private mapEl?: HTMLDivElement;

  private core?: Map0Core;
  private normalized?: NormalizedConfig;
  private popup?: Popup;
  @query("map0-bottom-sheet") private sheetEl?: Map0BottomSheet;
  /** the feature info on show, in either container (re-presented on a breakpoint change) */
  private info?: { lngLat: [number, number]; results: FeatureInfoResult[] };
  private resizeObserver?: ResizeObserver;
  private popups?: PopupRenderer;
  private createPopup?: (options?: object) => Popup;
  private unsubs: Array<() => void> = [];
  private initialized = false;
  private initStarted = false;
  private observer?: IntersectionObserver;
  /** bumped by every teardown; an init() whose token is stale must not attach */
  private generation = 0;
  /** the config source the running init() consumed (see `currentSource`) */
  private activeSource?: { kind: "config" | "configSrc" | "inline"; value?: unknown };

  /** imperative access to the running map (available after `map0:ready`) */
  get api(): Map0Core | undefined {
    return this.core;
  }

  /**
   * The search and a panel docked top-right share the top row, so the layout
   * has to know which of them is actually there — see the responsive block in
   * `styles.ts`. Both are mirrored onto `.stage` as `data-search`/`data-top-right`.
   */
  private get hasSearch(): boolean {
    return !!this.normalized && this.normalized.search !== false;
  }

  private get hasToc(): boolean {
    const cfg = this.normalized;
    if (!cfg || cfg.controls.layerSwitcher === false) return false;
    return cfg.layers.length > 0 || this._layers.length > 0 || cfg.controls.layerSwitcher.allowAdd;
  }

  /** the layer panel has a filter box — "auto" from TOC_FILTER_AUTO layers on (F2.7) */
  private get hasTocFilter(): boolean {
    const ls = this.normalized?.controls.layerSwitcher;
    if (!ls) return false;
    return ls.filter === "auto" ? this._layers.length >= TOC_FILTER_AUTO : ls.filter;
  }

  /** corner the layer panel docks to, null while there is none */
  private get tocCorner(): Corner | null {
    const ls = this.normalized?.controls.layerSwitcher;
    return ls && this.hasToc ? corner(ls.position) : null;
  }

  /** corner the legend docks to, null while it has nothing to show */
  private get legendCorner(): Corner | null {
    const legend = this.normalized?.controls.legend;
    if (!legend) return null;
    return this._layers.some((l) => l.visible && l.inZoomRange && l.legend) ? corner(legend.position) : null;
  }

  /** the corners in use — the top row and the measure bar make room for them */
  private get dockedCorners(): Corner[] {
    return [this.tocCorner, this.legendCorner].filter((c): c is Corner => c !== null);
  }

  /** users may add layers — the dialog's "+" and the file drop share this gate (F3.1, F3.2) */
  private get allowAdd(): boolean {
    const ls = this.normalized?.controls.layerSwitcher;
    return !!ls && ls.allowAdd;
  }

  protected override firstUpdated(): void {
    this.initialized = true;
    this.arm();
  }

  /**
   * `firstUpdated()` runs once per element, but the element can be taken out of
   * the DOM and put back (SPA routing, tabs, CMS re-renders, `appendChild` of a
   * live node). `disconnectedCallback()` tears the map down, so re-connecting
   * has to arm it again — otherwise the element comes back dead.
   */
  override connectedCallback(): void {
    super.connectedCallback();
    if (this.initialized && !this.initStarted) this.arm();
    this.observeWidth();
  }

  /** popup or sheet is decided by the element's own width, tracked while connected */
  private observeWidth(): void {
    if (typeof ResizeObserver === "undefined") return;
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? this.getBoundingClientRect().width;
      this.setNarrow(width < SHEET_BREAKPOINT);
      this._compact = width <= COMPACT_MAX;
    });
    this.resizeObserver.observe(this);
  }

  private setNarrow(narrow: boolean): void {
    if (narrow === this._narrow) return;
    this._narrow = narrow;
    /* an open answer moves into the other container rather than getting lost */
    if (this.info) this.presentInfo();
  }

  /** start now, or wait for the element to approach the viewport */
  private arm(): void {
    /* start early enough that the map is ready by the time it scrolls in */
    if (this.loading === "eager" || typeof IntersectionObserver === "undefined") {
      void this.init();
      return;
    }
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.load();
      },
      { rootMargin: "300px" },
    );
    this.observer.observe(this);
  }

  /** Start loading now, whatever `loading` says (for tabs, accordions, tests). */
  load(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    if (!this.initStarted) void this.init();
  }

  /**
   * Which of the three config sources would be used right now (first match
   * wins, as documented on the class). Comparing this to the source the running
   * viewer actually consumed is the only reliable reload trigger: looking at
   * the *previous* property value misses the case where a higher-priority
   * source appears for the first time — a viewer started from `config-src` that
   * is later handed a `config` object, for instance.
   */
  private currentSource(): { kind: "config" | "configSrc" | "inline"; value?: unknown } {
    if (this.config) return { kind: "config", value: this.config };
    if (this.configSrc) return { kind: "configSrc", value: this.configSrc };
    return { kind: "inline" };
  }

  protected override updated(changed: PropertyValues): void {
    /* the theme attribute restyles the running map in place — no reload */
    if (changed.has("theme") && this.normalized) this.applyTheme(this.normalized);
    /* while init() is still running too — the generation token retires it */
    if (!this.initialized || !this.initStarted || !this.activeSource) return;
    if (!changed.has("config") && !changed.has("configSrc")) return;
    const next = this.currentSource();
    if (next.kind !== this.activeSource.kind || next.value !== this.activeSource.value) {
      this.reload();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this.observer = undefined;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.teardown();
  }

  /** tear down and re-init (e.g. after assigning a new config) */
  reload(): void {
    this.teardown();
    void this.updateComplete.then(() => void this.init());
  }

  private teardown(): void {
    /* retires any init() still in flight: it checks the token after every await
       and destroys a core that finished for a generation nobody waits for */
    this.generation++;
    for (const u of this.unsubs) u();
    this.unsubs = [];
    this.statusSeen.clear();
    this.removePopup();
    this._sheet = null;
    this.info = undefined;
    this.core?.destroy();
    this.core = undefined;
    this.measureController = undefined; // destroyed via unsubs — never reuse it
    this.searchSeq++;
    clearTimeout(this.searchTimer);
    this.searchAbort?.abort();
    this.searchAbort = undefined;
    this.initStarted = false;
    this._notices = [];
    this._errors = null;
    this._fatal = null;
    this._layers = [];
    this._measure = null;
    this._searchResults = [];
    this._searchBusy = false;
    this._searchEmpty = false;
    this._searchActive = -1;
    this._hover = null;
    this._dropActive = false;
    this._ready = false;
    this._loading = false;
  }

  /** typed against the public event map (element.ts), so a new event or payload has to be declared there */
  private emit<K extends keyof Map0ViewerEventDetails>(name: K, detail: Map0ViewerEventDetails[K]): void {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  /** MapLibre's stylesheet arrives with the engine — prepend it so ours still wins */
  private adoptMaplibreCss(css: string): void {
    const root = this.shadowRoot;
    if (!root) return;
    maplibreSheet ??= (() => {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      return sheet;
    })();
    if (!root.adoptedStyleSheets.includes(maplibreSheet)) {
      root.adoptedStyleSheets = [maplibreSheet, ...root.adoptedStyleSheets];
    }
  }

  /** load a dialog's chunk, then open it (a slow network shows a busy cursor, not a blank panel) */
  private async openDialog(kind: "add" | "print"): Promise<void> {
    this._dialogLoading = kind;
    try {
      await loadDialog[kind]();
      if (kind === "add") this._addOpen = true;
      else this._printOpen = true;
    } catch (e) {
      this.notify("error", String(e));
    } finally {
      this._dialogLoading = null;
    }
  }

  /** dropped or picked feature files become geojson layers (F3.2); the pipeline loads on first use */
  private async importFiles(files: File[]): Promise<void> {
    const core = this.core;
    if (!core) return;
    try {
      const { importGeoFiles } = await import("./file-import.js");
      const { errors } = await importGeoFiles(core, files, core.t);
      for (const message of errors) this.notify("error", message);
    } catch (e) {
      this.notify("error", e instanceof Error ? e.message : String(e));
    }
  }

  /* -------------------------------- measuring ------------------------------- */

  /** activate a measure mode, loading the module on first use */
  private async startMeasure(mode: MeasureMode): Promise<void> {
    if (!this.core) return;
    this._measureLoading = true;
    try {
      if (!this.measureController) {
        const engine = await loadEngine();
        const { MeasureController } = await engine.loadMeasure();
        const controller = new MeasureController(
          this.core.map,
          this.normalized!.theme.primary,
          this.core.locale,
        );
        this.measureController = controller;
        this.unsubs.push(
          controller.state.subscribe((v) => (this._measure = v)),
          () => controller.destroy(),
        );
        /* the measurement must survive a basemap change like any other overlay */
        this.core.registerOverlay(() => controller.ids);
      }
      this.dismissInfo();
      this.core.setInteractionLocked(true);
      this.measureController.start(mode);
    } catch (e) {
      this.notify("error", String(e));
    } finally {
      this._measureLoading = false;
    }
  }

  private stopMeasure(): void {
    this.measureController?.stop();
    this.core?.setInteractionLocked(false);
    this._measure = null;
  }

  /* --------------------------------- search -------------------------------- */

  /** debounced query; the sequence guard keeps a slow answer from overwriting a newer one */
  private queueSearch(query: string): void {
    const seq = ++this.searchSeq;
    clearTimeout(this.searchTimer);
    this.searchAbort?.abort();
    const config = this.normalized?.search;
    const core = this.core;
    if (!config || !core) return;
    if (!query.trim()) {
      this._searchResults = [];
      this._searchEmpty = false;
      this._searchBusy = false;
      return;
    }
    this.searchTimer = setTimeout(() => {
      void (async () => {
        const abort = new AbortController();
        this.searchAbort = abort;
        this._searchBusy = true;
        try {
          const { search } = await loadEngine();
          if (seq !== this.searchSeq) return;
          const center = core.map.getCenter();
          const results = await search(config, query, {
            center: [center.lng, center.lat],
            lang: core.locale,
            signal: abort.signal,
          });
          if (seq !== this.searchSeq) return;
          this._searchResults = results;
          this._searchActive = results.length > 0 ? 0 : -1;
          this._searchEmpty = results.length === 0 && query.trim().length >= config.minLength;
        } catch (e) {
          if (seq === this.searchSeq && (e as Error)?.name !== "AbortError") {
            this._searchResults = [];
            this.notify("error", `${core.t("search.failed")}: ${(e as Error).message}`);
          }
        } finally {
          if (seq === this.searchSeq) this._searchBusy = false;
        }
      })();
    }, 250);
  }

  private pickSearchResult(result: SearchResult): void {
    this.cancelSearch();
    this.core?.showLocation({ center: result.center, bbox: result.bbox });
    this._searchResults = [];
    this._searchActive = -1;
    this._searchEmpty = false;
    this.emit("map0:search", { result });
  }

  private onSearchKey(event: KeyboardEvent): void {
    const count = this._searchResults.length;
    if (event.key === "ArrowDown" && count > 0) {
      event.preventDefault();
      this._searchActive = (this._searchActive + 1) % count;
    } else if (event.key === "ArrowUp" && count > 0) {
      event.preventDefault();
      this._searchActive = (this._searchActive - 1 + count) % count;
    } else if (event.key === "Enter") {
      const hit = this._searchResults[Math.max(this._searchActive, 0)];
      if (hit) this.pickSearchResult(hit);
    } else if (event.key === "Escape") {
      event.stopPropagation();
      this.cancelSearch();
      this._searchResults = [];
      this._searchEmpty = false;
      (event.target as HTMLInputElement).blur();
    }
  }

  private cancelSearch(): void {
    this.searchSeq++;
    clearTimeout(this.searchTimer);
    this.searchAbort?.abort();
    this._searchBusy = false;
  }

  /** transient notice toast (auto-dismisses; also used by copy/share actions) */
  notify(kind: "error" | "info", text: string): void {
    const id = ++this.noticeSeq;
    this._notices = [...this._notices, { id, kind, text }];
    setTimeout(() => {
      this._notices = this._notices.filter((n) => n.id !== id);
    }, 6000);
  }

  private async loadRawConfig(): Promise<unknown> {
    /* recorded synchronously, before the first await: updated() compares it
       against the source that would be picked now to decide about a reload */
    this.activeSource = this.currentSource();
    if (this.config) return resolveConfigExtends(this.config);
    if (this.configSrc) {
      const res = await fetch(this.configSrc);
      if (!res.ok) throw new Error(`could not fetch config: HTTP ${res.status} (${this.configSrc})`);
      /* relative `extends` URLs resolve against the config file's own URL */
      return resolveConfigExtends(await res.json(), { baseUrl: res.url || this.configSrc });
    }
    const inline = this.querySelector('script[type="application/json"]');
    if (inline?.textContent?.trim()) {
      try {
        return await resolveConfigExtends(JSON.parse(inline.textContent));
      } catch (e) {
        throw new Error(`config could not be loaded: ${e instanceof Error ? e.message : e}`);
      }
    }
    throw new Error(
      'no config found — set the "config" property, a "config-src" attribute, or an inline <script type="application/json"> child',
    );
  }

  private async init(): Promise<void> {
    /* A scheduled init() can arrive after the element left the DOM — `reload()`
       waits for the next update, and a disconnect in that window would otherwise
       start an initialisation that immediately gives up, leaving `initStarted`
       set. `connectedCallback()` reads that flag, so the element would never arm
       itself again: a viewer that survived removal but not removal-during-reload.
       Bailing out before the flag is set keeps it armable. */
    if (!this.isConnected) return;
    const gen = ++this.generation;
    /** true while this init() is still the one the element is waiting for */
    const current = (): boolean => gen === this.generation && this.isConnected;
    this.initStarted = true;
    this._loading = true;
    try {
      const raw = await this.loadRawConfig();
      if (!current()) return;
      const result = validateConfig(raw);
      /* forward compatibility (C5): keys this version does not know are logged
         and skipped — a config written for a newer map0 still opens */
      for (const w of result.warnings) console.warn(`[map0] ${w.path}: ${w.message}`);
      if (!result.valid) {
        this._errors = result.errors;
        this.emit("map0:error", { errors: result.errors });
        return;
      }
      const cfg = normalizeConfig(result.config!);
      this.normalized = cfg;
      this.applyTheme(cfg);

      const width = this.getBoundingClientRect().width;
      const ls = cfg.controls.layerSwitcher;
      this._tocOpen = ls === false ? false : ls.open === "auto" ? width >= 560 : ls.open;
      /* the observer keeps this current; without one, a single measurement has to do */
      if (!this.resizeObserver) {
        this._narrow = width < SHEET_BREAKPOINT;
        this._compact = width <= COMPACT_MAX;
      }
      this._legendOpen = cfg.controls.legend === false ? false : cfg.controls.legend.open;

      /* the engine, MapLibre and its stylesheet, and the popup renderer */
      const [{ createCore, createPopup }, popups, maplibreCss] = await Promise.all([
        loadEngine(),
        loadPopupRenderer(),
        loadMaplibreCss().then((m) => m.default),
      ]);
      if (!current()) return;
      this.createPopup = createPopup;
      this.popups = popups;
      this.adoptMaplibreCss(maplibreCss);

      await this.updateComplete;
      if (!current()) return;
      if (!this.mapEl) throw new Error("internal: map container missing");

      const core = await createCore({ container: this.mapEl, config: cfg, fullscreenTarget: this });
      /* the element was removed or re-configured while the engine was starting:
         the finished core belongs to nobody, so it must not be kept */
      if (!current()) {
        core.destroy();
        return;
      }
      this.core = core;
      this._basemapId = core.basemaps.current.value;
      this.unsubs.push(
        core.layers.state.subscribe(
          (v) => {
            this._layers = v;
            /* one toast per layer entering the error state (F2.5 feedback) */
            for (const l of v) {
              if (l.status === "error" && this.statusSeen.get(l.id) !== "error") {
                this.notify("error", `${l.title}: ${core.t("layers.error")}`);
              }
              this.statusSeen.set(l.id, l.status);
            }
          },
          { immediate: true },
        ),
        core.basemaps.current.subscribe((v) => (this._basemapId = v)),
        core.events.on("ready", () => {
          this.emit("map0:ready", { api: core });
        }),
        core.events.on("featureclick", (e) => this.showPopup(e)),
        core.events.on("featurehover", (e) => {
          this._hover = e
            ? { x: e.point[0], y: e.point[1], html: popups.sanitizeHtml(e.html) }
            : null;
        }),
        core.events.on("coordinates", (e) => this.showCoordinates(e)),
        core.events.on("error", (e) => {
          /* control failures (geolocate) surface as a toast — on a phone the
             button gives no other feedback than "nothing happened" */
          if (e.control) this.notify("error", e.message);
          this.emit("map0:error", e);
        }),
      );
      if (cfg.controls.print) {
        core.map.addControl(
          new IconButtonControl("print", () => void this.openDialog("print"), core.t("print.title")),
          "top-left",
        );
      }
      if (cfg.controls.measure) {
        core.map.addControl(
          new IconButtonControl(
            "measure",
            () => {
              if (this._measure) this.stopMeasure();
              else void this.startMeasure("distance");
            },
            core.t("measure.title"),
          ),
          "top-left",
        );
      }
      if (cfg.permalink !== false) {
        core.map.addControl(
          new IconButtonControl(
            "share",
            () => {
              const url = core.getShareUrl();
              if (!url) return;
              void navigator.clipboard
                .writeText(url)
                .then(() => this.notify("info", core.t("share.copied")))
                .catch(() => this.notify("error", core.t("coords.copyFailed")));
            },
            core.t("share.title"),
          ),
          "top-left",
        );
      }
      /* local feature files, dropped anywhere on the viewer (F3.2) */
      this.unsubs.push(
        attachDropZone(this, {
          accepts: () => this.allowAdd && !!this.core,
          onActive: (active) => (this._dropActive = active),
          onFiles: (files) => void this.importFiles(files),
        }),
      );
      /* dismiss the loading veil on the first painted frame after the style is in —
         waiting for `load` (all initial tiles) can take long on slow WMS servers */
      core.map.once("style.load", () => core.map.once("render", () => (this._ready = true)));
      const readyFallback = setTimeout(() => (this._ready = true), 12_000);
      this.unsubs.push(() => clearTimeout(readyFallback));
    } catch (e) {
      if (!current()) return; // a failure nobody is waiting for any more
      this._fatal = e instanceof Error ? e.message : String(e);
      this.emit("map0:error", { message: this._fatal });
    }
  }

  /** removes the previous scheme listener when applyTheme runs again (attribute flip) */
  private themeUnsub?: () => void;

  private applyTheme(cfg: NormalizedConfig): void {
    this.style.setProperty("--map0-primary", cfg.theme.primary);
    this.style.setProperty("--map0-radius", RADII[cfg.theme.radius]);
    this.style.setProperty("--map0-radius-sm", RADII_SM[cfg.theme.radius]);
    if (cfg.theme.font) this.style.setProperty("--map0-font", cfg.theme.font);

    this.themeUnsub?.();
    this.themeUnsub = undefined;
    const apply = (dark: boolean) => this.setAttribute("data-theme", dark ? "dark" : "light");
    if (this.theme === "dark" || this.theme === "light") {
      apply(this.theme === "dark");
    } else if (cfg.theme.mode === "auto" && typeof matchMedia !== "undefined") {
      const mq = matchMedia("(prefers-color-scheme: dark)");
      const listener = (e: MediaQueryListEvent) => apply(e.matches);
      mq.addEventListener("change", listener);
      this.themeUnsub = () => mq.removeEventListener("change", listener);
      this.unsubs.push(this.themeUnsub);
      apply(mq.matches);
    } else {
      apply(cfg.theme.mode === "dark");
    }
  }

  /* ------------------------------ feature info ------------------------------ */

  private showPopup(e: { lngLat: [number, number]; results: FeatureInfoResult[] }): void {
    if (!this.core) return;
    /* a tap that hit nothing puts the previous answer away (the anchored popup
       already closed itself on the click; the sheet has to be told) */
    if (e.results.length === 0) {
      this.dismissInfo();
      return;
    }
    this.info = e;
    this.presentInfo();
    this.emit("map0:featureclick", e);
  }

  /**
   * Show `info` in the container the viewer's width calls for: an anchored
   * MapLibre popup, or below SHEET_BREAKPOINT a bottom sheet (F5.5). Same
   * sanitized markup either way — only the box differs.
   */
  private presentInfo(): void {
    const info = this.info;
    if (!info || !this.core || !this.popups || !this.createPopup) return;
    const content = this.popups.buildPopupContent(info.results, this.core.t);
    /* swapping containers must not read as "closed": the highlight stays */
    this.removePopup();
    if (this._narrow) {
      this._sheet = { label: this.popups.describeResults(info.results), content };
      return;
    }
    this._sheet = null;
    this.popup = this.createPopup({ closeButton: true, maxWidth: "340px" })
      .setLngLat(info.lngLat)
      .setDOMContent(content)
      .addTo(this.core.map);
    this.popup.on("close", this.onPopupClose);
  }

  /** the anchored popup was closed by the user (its close button, or a click on the map) */
  private onPopupClose = (): void => {
    this.popup = undefined;
    this.info = undefined;
    this.core?.clearHighlight();
  };

  /** close whatever feature info is showing and drop its highlight */
  private dismissInfo(): void {
    this.info = undefined;
    this.removePopup();
    this.core?.clearHighlight();
    /* the sheet slides out first and reports back through `close` — the same
       exit whether the user dismissed it or a tap on the map did */
    if (this.sheetEl) this.sheetEl.close();
    else this._sheet = null;
  }

  /**
   * The sheet is done sliding out — after its close button, Escape, a drag down,
   * or `dismissInfo()`. Focus returns to the map when it was in the sheet; a
   * control the user is on (the measure button, say) keeps it.
   */
  private onSheetClose(): void {
    this.info = undefined;
    this.core?.clearHighlight();
    const active = this.shadowRoot?.activeElement ?? null;
    if (active && this.sheetEl?.contains(active)) {
      this.core?.map.getCanvas().focus({ preventScroll: true });
    }
    this._sheet = null;
  }

  /** remove the anchored popup without it counting as a user close */
  private removePopup(): void {
    if (!this.popup) return;
    this.popup.off("close", this.onPopupClose);
    this.popup.remove();
    this.popup = undefined;
  }

  private showCoordinates(e: {
    lngLat: [number, number];
    entries: Array<{ code: string; label: string; text: string }>;
  }): void {
    if (!this.core || !this.createPopup) return;
    const t = this.core.t;
    this.dismissInfo();
    /* built entirely with textContent — nothing here comes from remote services */
    const container = document.createElement("div");
    container.className = "m0-popup m0-coords";
    const heading = document.createElement("p");
    heading.className = "m0-layer-title";
    heading.textContent = t("coords.title");
    container.appendChild(heading);
    const table = document.createElement("table");
    table.className = "m0-fields";
    for (const entry of e.entries) {
      const row = table.insertRow();
      const th = document.createElement("th");
      th.scope = "row";
      th.textContent = entry.label;
      th.title = entry.code;
      row.appendChild(th);
      const td = row.insertCell();
      const value = document.createElement("span");
      value.textContent = entry.text;
      td.appendChild(value);
      const copy = document.createElement("button");
      copy.className = "icon-btn copy-btn";
      copy.title = t("coords.copy");
      copy.setAttribute("aria-label", `${t("coords.copy")} (${entry.label})`);
      copy.textContent = "⧉";
      copy.addEventListener("click", () => {
        void navigator.clipboard
          .writeText(entry.text)
          .then(() => this.notify("info", t("coords.copied")))
          .catch(() => this.notify("error", t("coords.copyFailed")));
      });
      td.appendChild(copy);
    }
    container.appendChild(table);
    this.popup = this.createPopup({ closeButton: true, maxWidth: "340px" })
      .setLngLat(e.lngLat)
      .setDOMContent(container)
      .addTo(this.core.map);
  }

  /* --------------------------------- render -------------------------------- */

  protected override render(): TemplateResult {
    if (this._fatal) return this.renderErrors([{ path: "$", message: this._fatal }]);
    if (this._errors) return this.renderErrors(this._errors);
    return html`
      <div
        class="stage"
        ?data-busy=${this._dialogLoading !== null}
        ?data-search=${this.hasSearch}
        ?data-top-right=${this.dockedCorners.some((c) => c.end === "top" && (c.edge === "right" || this._compact))}
        data-dock=${dockSides(this.dockedCorners, this._compact) || nothing}
        ?data-measuring=${this._measure !== null}
      >
        <div class="map" aria-label=${this.normalized?.meta.title ?? "Karte"}></div>
        <div class="loading" ?data-done=${this._ready} aria-hidden="true">
          ${this._loading ? html`<div class="spinner"></div>` : nothing}
        </div>
        ${this._hover
          ? html`<div
              class="hover-tip"
              style=${`left:${this._hover.x + 12}px;top:${this._hover.y + 12}px`}
            >
              ${unsafeHTML(this._hover.html)}
            </div>`
          : nothing}
        ${this._dropActive
          ? html`<div class="drop-zone" aria-hidden="true">
              <span>${this.core?.t("addLayer.dropHint")}</span>
            </div>`
          : nothing}
        ${this.renderSearch()} ${this.renderDocks()} ${this.renderBasemaps()}
        ${this.renderMeasure()}
        ${this._addOpen && this.core
          ? html`<map0-add-layer
              .core=${this.core}
              .t=${this.core.t}
              @close=${() => (this._addOpen = false)}
            ></map0-add-layer>`
          : nothing}
        ${this._printOpen && this.core
          ? html`<map0-print
              .core=${this.core}
              .t=${this.core.t}
              @close=${() => (this._printOpen = false)}
            ></map0-print>`
          : nothing}
        ${this._sheet && this.core
          ? html`<map0-bottom-sheet
              .content=${this._sheet.content}
              .label=${this._sheet.label}
              .t=${this.core.t}
              @close=${() => this.onSheetClose()}
            ></map0-bottom-sheet>`
          : nothing}
        ${this._notices.length > 0
          ? html`<div class="notices" role="status" aria-live="polite">
              ${this._notices.map(
                (n) => html`
                  <div class="notice" data-kind=${n.kind}>
                    <span>${n.text}</span>
                    <button
                      class="icon-btn"
                      aria-label=${this.core?.t("popup.close") ?? "Close"}
                      @click=${() =>
                        (this._notices = this._notices.filter((x) => x.id !== n.id))}
                    >
                      ✕
                    </button>
                  </div>
                `,
              )}
            </div>`
          : nothing}
      </div>
    `;
  }

  private renderMeasure(): TemplateResult | typeof nothing {
    const m = this._measure;
    if (!m) return nothing;
    const t = this.core?.t ?? ((k: string) => k);
    const started = m.points.length > 0;
    return html`
      <div class="measure-bar panel" part="measure" role="group" aria-label=${t("measure.title")}>
        <div class="measure-modes" role="radiogroup">
          ${(["distance", "area"] as const).map(
            (mode) => html`
              <button
                type="button"
                role="radio"
                aria-checked=${m.mode === mode ? "true" : "false"}
                ?data-active=${m.mode === mode}
                @click=${() => void this.startMeasure(mode)}
              >
                ${t(`measure.${mode}`)}
              </button>
            `,
          )}
        </div>
        <div class="measure-readout">
          ${started
            ? html`<strong>${m.text}</strong>
                ${m.perimeter
                  ? html`<span class="measure-sub">${t("measure.perimeter")} ${m.perimeter}</span>`
                  : nothing}`
            : html`<span class="measure-hint">${t("measure.hint")}</span>`}
        </div>
        ${started
          ? html`<button class="btn measure-clear" @click=${() => void this.startMeasure(m.mode)}>
              ${t("measure.clear")}
            </button>`
          : nothing}
        <button
          class="icon-btn"
          aria-label=${t("measure.close")}
          title=${t("measure.close")}
          @click=${() => this.stopMeasure()}
        >
          ✕
        </button>
      </div>
    `;
  }

  private renderSearch(): TemplateResult | typeof nothing {
    const cfg = this.normalized;
    if (!cfg || cfg.search === false) return nothing;
    const t = this.core?.t ?? ((k: string) => k);
    const open = this._searchResults.length > 0 || this._searchEmpty;
    return html`
      <div class="search" part="search" ?data-expanded=${this._searchExpanded}>
        <div
          class="search-field"
          @click=${(e: Event) => {
            /* Collapsed (narrow containers), the input is 0px wide — nothing
               tappable ever focuses it, so the search could not be opened at
               all on phones. A tap anywhere on the field expands it and hands
               the input focus (state set here too: an unfocused document
               swallows the focus event the input's @focus relies on). */
            this._searchExpanded = true;
            (e.currentTarget as HTMLElement).querySelector("input")?.focus();
          }}
        >
          ${icons.search}
          <input
            type="search"
            role="combobox"
            aria-expanded=${open ? "true" : "false"}
            aria-controls="m0-search-list"
            aria-label=${t("search.label")}
            placeholder=${cfg.search.placeholder ?? t("search.placeholder")}
            autocomplete="off"
            @focus=${() => (this._searchExpanded = true)}
            @blur=${() => setTimeout(() => (this._searchExpanded = false), 150)}
            @input=${(e: Event) => this.queueSearch((e.target as HTMLInputElement).value)}
            @keydown=${(e: KeyboardEvent) => this.onSearchKey(e)}
          />
          ${this._searchBusy ? html`<span class="search-spinner" aria-hidden="true"></span>` : nothing}
        </div>
        ${open
          ? html`<ul class="search-results" id="m0-search-list" role="listbox">
              ${this._searchEmpty
                ? html`<li class="search-empty">${t("search.noResults")}</li>`
                : this._searchResults.map(
                    (r, i) => html`
                      <li
                        role="option"
                        aria-selected=${i === this._searchActive ? "true" : "false"}
                        ?data-active=${i === this._searchActive}
                        @mousedown=${(e: Event) => {
                          e.preventDefault(); // keep focus, blur would close the list first
                          this.pickSearchResult(r);
                        }}
                        @mouseenter=${() => (this._searchActive = i)}
                      >
                        <span class="search-label">
                          ${r.isCoordinate ? icons.pin : nothing}${r.label}
                        </span>
                        ${r.detail ? html`<span class="search-detail">${r.detail}</span>` : nothing}
                      </li>
                    `,
                  )}
            </ul>`
          : nothing}
      </div>
    `;
  }

  /** `end`: docked at the bottom corner of its edge */
  private renderLegend(end = false): TemplateResult | typeof nothing {
    const cfg = this.normalized;
    if (!cfg || cfg.controls.legend === false) return nothing;
    const items = this._layers.filter((l) => l.visible && l.inZoomRange && l.legend);
    if (items.length === 0) return nothing;
    const t = this.core?.t ?? ((k: string) => k);
    return html`
      <div
        class="legend panel"
        part="legend"
        ?data-open=${this._legendOpen}
        ?data-end=${end}
        ?data-left=${this.legendCorner?.edge === "left"}
      >
        <button
          class="panel-header"
          @click=${() => (this._legendOpen = !this._legendOpen)}
          aria-expanded=${this._legendOpen ? "true" : "false"}
        >
          ${icons.legend}<span>${t("legend.title")}</span>${icons.chevron}
        </button>
        ${this._legendOpen
          ? html`<div class="panel-body">
              ${items.map(
                (l) => html`
                  <div class="legend-layer">
                    <div class="legend-layer-title">${l.title}</div>
                    ${l.legend!.kind === "image"
                      ? html`<img
                          class="legend-image"
                          src=${l.legend!.url}
                          alt=${l.title}
                          loading="lazy"
                          @error=${(e: Event) =>
                            ((e.target as HTMLElement).style.display = "none")}
                        />`
                      : l.legend!.entries.map(
                          (en) => html`
                            <div class="legend-entry">
                              ${en.image
                                ? html`<img class="legend-entry-img" src=${en.image} alt="" />`
                                : html`<span
                                    class="swatch"
                                    data-shape=${en.shape}
                                    style=${`--swatch:${en.color ?? "var(--map0-muted)"}`}
                                  ></span>`}
                              ${en.label ? html`<span>${en.label}</span>` : nothing}
                            </div>
                          `,
                        )}
                  </div>
                `,
              )}
            </div>`
          : nothing}
      </div>
    `;
  }

  private renderErrors(errors: ValidationError[]): TemplateResult {
    const t = this.core?.t ?? ((k: string) => (k === "error.title" ? "map0 configuration error" : "The map could not start. Please fix the configuration:"));
    return html`
      <div class="stage">
        <div class="error-panel" role="alert">
          <div class="error-card">
            <h3>${t("error.title")}</h3>
            <p>${t("error.intro")}</p>
            <ul>
              ${errors.map((e) => html`<li><code>${e.path}</code> — ${e.message}</li>`)}
            </ul>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * The layer panel and the legend, docked to the edge their `position`
   * names: one column per edge, the top corner's panels first and the bottom
   * corner's last, so two panels on one edge share its height. A compact
   * viewer has room for one full-width column only, so everything goes there.
   */
  private renderDocks(): unknown[] {
    const panels = [
      { corner: this.tocCorner, render: (end: boolean) => this.renderToc(end) },
      { corner: this.legendCorner, render: (end: boolean) => this.renderLegend(end) },
    ];
    const sides = this._compact ? (["right"] as const) : EDGES;
    return sides.map((side) => {
      const here = panels.filter((p) => p.corner && (this._compact || p.corner.edge === side));
      if (here.length === 0) return nothing;
      const top = here.filter((p) => p.corner!.end === "top").map((p) => p.render(false));
      const bottom = here.filter((p) => p.corner!.end === "bottom").map((p) => p.render(true));
      return html`<div class="dock" part=${`dock dock-${side}`} data-side=${side}>${top}${bottom}</div>`;
    });
  }

  /** `end`: docked at the bottom corner of its edge */
  private renderToc(end = false): TemplateResult | typeof nothing {
    const cfg = this.normalized;
    if (!cfg || cfg.controls.layerSwitcher === false || !this.hasToc) return nothing;
    const allowAdd = this.allowAdd;
    const t = this.core?.t ?? ((k: string) => k);
    const byId = new Map(this._layers.map((l) => [l.id, l]));
    const terms = this.hasTocFilter ? filterTerms(this._tocFilter) : [];
    const extras = this._layers.filter((l) => l.userAdded && matchesAll(l.title, terms));
    /* pruned once: what renders is what the group boxes act on */
    const toc =
      terms.length > 0
        ? cfg.toc.map((n) => this.pruneToc(n, byId, terms)).filter((n): n is TocNode => n !== null)
        : cfg.toc;
    const noMatch = terms.length > 0 && extras.length === 0 && toc.length === 0;
    const title = cfg.controls.layerSwitcher.title ?? t("layers.title");
    return html`
      <div
        class="toc panel"
        part="toc"
        ?data-open=${this._tocOpen}
        ?data-end=${end}
        ?data-left=${this.tocCorner?.edge === "left"}
      >
        <button
          class="panel-header"
          @click=${() => (this._tocOpen = !this._tocOpen)}
          aria-expanded=${this._tocOpen ? "true" : "false"}
        >
          ${icons.layers}<span>${title}</span>
          ${allowAdd
            ? html`<span
                class="icon-btn add-btn"
                role="button"
                tabindex="0"
                title=${t("addLayer.title")}
                aria-label=${t("addLayer.title")}
                @click=${(e: Event) => {
                  e.stopPropagation();
                  void this.openDialog("add");
                }}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    void this.openDialog("add");
                  }
                }}
                >${icons.plus}</span
              >`
            : nothing}
          ${icons.chevron}
        </button>
        ${this._tocOpen
          ? html`<div class="panel-body">
              ${this.hasTocFilter ? this.renderTocFilter() : nothing}
              ${extras.length > 0
                ? html`<div class="toc-extras">
                    <div class="group-label">${t("layers.added")}</div>
                    ${extras.map((l) => this.renderLayerRow(l, terms))}
                  </div>`
                : nothing}
              ${this.renderNodes(toc, byId, "", terms)}
              ${noMatch ? html`<div class="toc-empty" role="status">${t("layers.noMatch")}</div>` : nothing}
              ${cfg.layers.length === 0 && this._layers.length === 0
                ? html`<div class="toc-empty">${t("layers.empty")}</div>`
                : nothing}
            </div>`
          : nothing}
      </div>
    `;
  }

  private renderTocFilter(): TemplateResult {
    const t = this.core?.t ?? ((k: string) => k);
    return html`
      <div class="toc-filter">
        ${icons.search}
        <input
          type="search"
          autocomplete="off"
          spellcheck="false"
          .value=${this._tocFilter}
          placeholder=${t("layers.filter")}
          aria-label=${t("layers.filter")}
          @input=${(e: Event) => (this._tocFilter = (e.target as HTMLInputElement).value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === "Escape" && this._tocFilter) {
              e.preventDefault();
              e.stopPropagation();
              this._tocFilter = "";
            }
          }}
        />
      </div>
    `;
  }

  /**
   * The part of a node the filter terms keep, null when nothing matches. A
   * group's title takes its share of the terms; one that matches them all
   * keeps the whole group.
   */
  private pruneToc(node: TocNode, byId: Map<string, LayerUIState>, terms: string[]): TocNode | null {
    if (node.kind === "layer") {
      const l = byId.get(node.layerId);
      return l && matchesAll(l.title, terms) ? node : null;
    }
    const rest = unmatchedTerms(node.title, terms);
    if (rest.length === 0) return node;
    const children = node.children
      .map((c) => this.pruneToc(c, byId, rest))
      .filter((c): c is TocNode => c !== null);
    return children.length > 0 ? { ...node, children } : null;
  }

  /**
   * The tree as the filter left it (pruneToc). `terms` highlight the hits;
   * `radio` names the exclusive group these nodes are the options of.
   */
  private renderNodes(
    nodes: TocNode[],
    byId: Map<string, LayerUIState>,
    path: string,
    terms: string[],
    radio?: string,
  ): unknown[] {
    const t = this.core?.t ?? ((k: string) => k);
    const isVisible = (id: string) => byId.get(id)?.visible ?? false;
    return nodes.map((n, i) => {
      if (n.kind === "group") {
        const key = `${path}/${i}:${n.title}`;
        /* a filtered tree shows its hits, collapsed or not — and takes no
           collapse clicks, so the keys (indices of the pruned list) stay out
           of _groupCollapsed */
        const filtered = terms.length > 0;
        const collapsed = filtered ? false : (this._groupCollapsed[key] ?? n.collapsed);
        const state = tocVisibility(n, isVisible);
        return html`
          <div class="group" ?data-collapsed=${collapsed}>
            <div class="group-header">
              <input
                type=${radio ? "radio" : "checkbox"}
                name=${radio ?? nothing}
                .checked=${live(radio ? state !== "off" : state === "on")}
                .indeterminate=${live(!radio && state === "mixed")}
                aria-label=${`${t(radio ? "layers.showGroup" : "layers.toggleGroup")}: ${n.title}`}
                @change=${(e: Event) => this.toggleGroup(n, (e.target as HTMLInputElement).checked)}
              />
              <button
                class="group-toggle"
                ?disabled=${filtered}
                @click=${() =>
                  (this._groupCollapsed = { ...this._groupCollapsed, [key]: !collapsed })}
                aria-expanded=${collapsed ? "false" : "true"}
              >
                <span>${highlight(n.title, terms)}</span>${icons.chevron}
              </button>
            </div>
            <div
              class="group-body"
              role=${n.exclusive ? "radiogroup" : nothing}
              aria-label=${n.exclusive ? n.title : nothing}
            >
              ${this.renderNodes(n.children, byId, key, terms, n.exclusive ? key : undefined)}
            </div>
          </div>
        `;
      }
      const l = byId.get(n.layerId);
      return l ? this.renderLayerRow(l, terms, radio) : nothing;
    });
  }

  /**
   * The group's box: on switches all of it on (one option of an exclusive
   * group), off switches every layer below it off (F2.3).
   */
  private toggleGroup(node: TocNode, on: boolean): void {
    const core = this.core;
    if (!core) return;
    const visible = new Map(this._layers.map((l) => [l.id, l.visible]));
    const isVisible = (id: string) => visible.get(id) ?? false;
    const show = new Set(on ? tocShowIds(node, isVisible) : []);
    for (const id of tocLeafIds(node)) {
      if (isVisible(id) && !show.has(id)) core.setLayerVisibility(id, false);
    }
    for (const id of show) if (!isVisible(id)) core.setLayerVisibility(id, true);
  }

  private renderLayerRow(l: LayerUIState, terms: string[] = [], radio?: string): TemplateResult {
    const t = this.core?.t ?? ((k: string) => k);
    const outOfRange = l.visible && !l.inZoomRange;
    return html`
      <div class="layer-row" ?data-out-of-range=${outOfRange}>
        <div class="layer-main">
          <label>
            <input
              type=${radio ? "radio" : "checkbox"}
              name=${radio ?? nothing}
              .checked=${l.visible}
              @change=${(e: Event) =>
                this.core?.setLayerVisibility(l.id, (e.target as HTMLInputElement).checked)}
            />
            <span class="layer-title" title=${l.title}>${highlight(l.title, terms)}</span>
          </label>
          ${outOfRange
            ? nothing
            : html`<span
                class="status-dot"
                data-status=${l.status}
                title=${l.status === "error" ? t("layers.error") : ""}
              ></span>`}
          ${l.canZoom
            ? html`<button
                class="icon-btn zoom-btn"
                title=${t("layers.zoomTo")}
                aria-label=${t("layers.zoomTo")}
                @click=${() => void this.core?.zoomToLayer(l.id)}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 8V5a2 2 0 0 1 2-2h3"/><path d="M16 3h3a2 2 0 0 1 2 2v3"/><path d="M21 16v3a2 2 0 0 1-2 2h-3"/><path d="M8 21H5a2 2 0 0 1-2-2v-3"/><circle cx="12" cy="12" r="2.5"/></svg>
              </button>`
            : nothing}
          ${l.metadataUrl
            ? html`<a
                class="meta-link"
                href=${l.metadataUrl}
                target="_blank"
                rel="noopener noreferrer"
                title=${l.metadataTitle ?? t("layers.metadata")}
                aria-label=${l.metadataTitle ?? t("layers.metadata")}
                >${icons.info}</a
              >`
            : nothing}
          ${l.userAdded
            ? html`<button
                class="icon-btn remove-btn"
                title=${t("layers.remove")}
                aria-label=${t("layers.remove")}
                @click=${() => this.core?.removeLayer(l.id)}
              >
                ✕
              </button>`
            : nothing}
        </div>
        ${outOfRange
          ? html`
              <div class="zoom-hint">
                ${l.minZoom !== undefined && (this.core?.map.getZoom() ?? 0) < l.minZoom
                  ? html`${t("layers.zoomHintMin")} ${l.minZoom}`
                  : html`${t("layers.zoomHintMax")} ${l.maxZoom}`}
              </div>
            `
          : nothing}
        ${l.visible
          ? html`
              <div class="opacity-row">
                <input
                  type="range"
                  min="0"
                  max="100"
                  .value=${String(Math.round(l.opacity * 100))}
                  aria-label=${t("layers.opacity")}
                  @input=${(e: Event) =>
                    this.core?.setLayerOpacity(
                      l.id,
                      Number((e.target as HTMLInputElement).value) / 100,
                    )}
                />
                <span class="opacity-value">${Math.round(l.opacity * 100)}%</span>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private renderBasemaps(): TemplateResult | typeof nothing {
    const cfg = this.normalized;
    if (!cfg || cfg.controls.basemapSwitcher === false || cfg.basemaps.length < 2) return nothing;
    return html`
      <div class="basemaps" part="basemaps" role="radiogroup" aria-label=${this.core?.t("basemaps.title") ?? "Basemap"}>
        ${cfg.basemaps.map(
          (b) => html`
            <button
              role="radio"
              aria-checked=${this._basemapId === b.id ? "true" : "false"}
              ?data-active=${this._basemapId === b.id}
              @click=${() => this.core?.setBasemap(b.id)}
            >
              ${b.title}
            </button>
          `,
        )}
      </div>
    `;
  }
}
